import { type PointerEvent as ReactPointerEvent, type WheelEvent as ReactWheelEvent, useEffect, useMemo, useRef, useState } from "react";

import type { V3ProvenanceCluster, V3ProvenanceEdge, V3ProvenanceGraph, V3ProvenanceNode } from "./types";

type DeckLayer = unknown;

type DeckConstructor = new (props: Record<string, unknown>) => {
  setProps: (props: Record<string, unknown>) => void;
  finalize: () => void;
};

type DeckLayerConstructor = new (props: Record<string, unknown>) => DeckLayer;

type DeckModule = {
  Deck: DeckConstructor;
  OrthographicView: new (props: Record<string, unknown>) => unknown;
  ScatterplotLayer: DeckLayerConstructor;
  LineLayer: DeckLayerConstructor;
  TextLayer?: DeckLayerConstructor;
};

export type V3DeckViewState = {
  target: [number, number, number];
  zoom: number;
};

export type V3DagProjectionMode = "plan" | "depth";

export type V3DagPinnedPosition = {
  x: number;
  y: number;
  z?: number;
};

export type V3DagLayoutState = {
  projectionMode: V3DagProjectionMode;
  depthStrength: number;
  perspective: number;
  nodePins: Record<string, V3DagPinnedPosition>;
  clusterPins: Record<string, V3DagPinnedPosition>;
  memberships: Record<string, string[]>;
};

export const DEFAULT_V3_DAG_LAYOUT: V3DagLayoutState = {
  projectionMode: "depth",
  depthStrength: 0.68,
  perspective: 0.34,
  nodePins: {},
  clusterPins: {},
  memberships: {},
};

export type V3DagProjectedPosition = {
  x: number;
  y: number;
  z: number;
  scale: number;
};

type DeckNodeDatum = {
  id: string;
  position: [number, number, number];
  projected: V3DagProjectedPosition;
  color: [number, number, number, number];
  lineColor: [number, number, number, number];
  lineWidth: number;
  radius: number;
  label: string;
};

type DeckEdgeDatum = {
  id: string;
  source: [number, number, number];
  target: [number, number, number];
  color: [number, number, number, number];
  width: number;
};

type DeckClusterDatum = {
  id: string;
  label: string;
  position: [number, number, number];
  projected: V3DagProjectedPosition;
  labelPosition: [number, number, number];
  fillColor: [number, number, number, number];
  lineColor: [number, number, number, number];
  textColor: [number, number, number, number];
  radius: number;
};

type DeckMembershipDatum = {
  id: string;
  source: [number, number, number];
  target: [number, number, number];
  color: [number, number, number, number];
  width: number;
};

type DeckPickingInfo = {
  object?: DeckNodeDatum | DeckClusterDatum;
  coordinate?: [number, number, number];
};

function normalizeViewState(value: Partial<V3DeckViewState> | undefined, fallback: V3DeckViewState): V3DeckViewState {
  const target = Array.isArray(value?.target) ? value.target : fallback.target;
  return {
    target: [
      Number(target[0] ?? fallback.target[0]),
      Number(target[1] ?? fallback.target[1]),
      Number(target[2] ?? fallback.target[2]),
    ],
    zoom: Number(value?.zoom ?? fallback.zoom),
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

let deckGlPromise: Promise<DeckModule> | null = null;

// deck.gl is bundled by Vite (see package.json dependency) and loaded via a
// dynamic import so it lands in its own async chunk instead of being pulled
// off a public CDN at runtime.
function loadDeckGl(): Promise<DeckModule> {
  if (!deckGlPromise) {
    deckGlPromise = import("deck.gl").then((mod) => {
      const deck = mod as unknown as DeckModule;
      if (!deck.Deck) throw new Error("deck.gl did not initialize");
      return deck;
    });
  }
  return deckGlPromise;
}

function nodeColor(node: V3ProvenanceNode, lit: boolean, selected: boolean, faded: boolean): [number, number, number, number] {
  if (selected) return [255, 255, 255, 245];
  if (lit) return [15, 255, 198, 235];
  if (faded) return [78, 98, 118, 58];
  if (node.type === "plot" || node.type === "plot_point" || node.type === "plot_trace") return [103, 215, 255, 190];
  if (node.type === "deck" || node.type === "slide" || node.type === "claim") return [155, 107, 255, 188];
  if (node.type === "parameter_atom" || node.type === "quantity_value") return [103, 232, 165, 185];
  return [164, 185, 198, 132];
}

function edgeColor(lit: boolean, faded: boolean): [number, number, number, number] {
  if (lit) return [15, 255, 198, 118];
  if (faded) return [70, 88, 105, 34];
  return [74, 178, 192, 76];
}

function depthForCluster(cluster: V3ProvenanceCluster | undefined): number {
  const key = `${cluster?.id ?? ""}:${cluster?.type ?? ""}`.toLowerCase();
  if (key.includes("source")) return -1.18;
  if (key.includes("condition") || key.includes("parameter")) return -0.82;
  if (key.includes("geometry") || key.includes("design")) return -0.54;
  if (key.includes("mesh")) return -0.28;
  if (key.includes("model")) return -0.18;
  if (key.includes("run") || key.includes("solver")) return 0.06;
  if (key.includes("operation") || key.includes("script") || key.includes("post")) return 0.22;
  if (key.includes("quantity") || key.includes("result")) return 0.44;
  if (key.includes("plot")) return 0.72;
  if (key.includes("presentation") || key.includes("deck") || key.includes("claim")) return 1.02;
  if (key.includes("artifact")) return 0.9;
  return 0;
}

function depthForNode(node: V3ProvenanceNode, graph: V3ProvenanceGraph): number {
  const cluster = graph.clusters.find((item) => item.id === node.cluster);
  const clusterDepth = depthForCluster(cluster);
  if (node.type === "source_dataset") return Math.min(clusterDepth, -1.18);
  if (node.type === "parameter_atom") return -0.86;
  if (node.type === "geometry") return -0.55;
  if (node.type === "mesh") return -0.3;
  if (node.type === "solver_run") return 0.08;
  if (node.type === "quantity_value" || node.type === "correction" || node.type === "operation") return 0.46;
  if (node.type === "plot" || node.type === "plot_trace" || node.type === "plot_point") return 0.76;
  if (node.type === "deck" || node.type === "slide" || node.type === "claim") return 1.05;
  if (node.type === "chat_thread" || node.type === "email_thread" || node.type === "smart_summary") return 0.94;
  return clusterDepth;
}

function projectBasePoint(position: { x: number; y: number }, depth: number, layout: V3DagLayoutState): V3DagProjectedPosition {
  const z = layout.projectionMode === "depth" ? depth * layout.depthStrength : 0;
  const perspective = layout.projectionMode === "depth" ? layout.perspective : 0;
  const center = { x: 640, y: 310 };
  const scale = Math.max(0.62, 1 + z * perspective);
  return {
    x: Number((center.x + (position.x - center.x) * scale + z * 52).toFixed(2)),
    y: Number((center.y + (position.y - center.y) * (1 + z * perspective * 0.42) - z * 44).toFixed(2)),
    z: Number(z.toFixed(3)),
    scale,
  };
}

export function v3DagProjectedClusterPosition(
  cluster: V3ProvenanceCluster,
  layout: V3DagLayoutState = DEFAULT_V3_DAG_LAYOUT,
): V3DagProjectedPosition {
  const pinned = layout.clusterPins[cluster.id];
  if (pinned) {
    const z = pinned.z ?? projectBasePoint(cluster.position, depthForCluster(cluster), layout).z;
    return { x: pinned.x, y: pinned.y, z, scale: Math.max(0.62, 1 + z * layout.perspective) };
  }
  return projectBasePoint(cluster.position, depthForCluster(cluster), layout);
}

export function v3DagProjectedNodePosition(
  node: V3ProvenanceNode,
  graph: V3ProvenanceGraph,
  layout: V3DagLayoutState = DEFAULT_V3_DAG_LAYOUT,
): V3DagProjectedPosition {
  const pinned = layout.nodePins[node.id];
  if (pinned) {
    const z = pinned.z ?? projectBasePoint(node.position, depthForNode(node, graph), layout).z;
    return { x: pinned.x, y: pinned.y, z, scale: Math.max(0.62, 1 + z * layout.perspective) };
  }
  const projected = projectBasePoint(node.position, depthForNode(node, graph), layout);
  const cluster = graph.clusters.find((item) => item.id === node.cluster);
  if (!cluster || !layout.clusterPins[node.cluster]) return projected;
  const originalCluster = projectBasePoint(cluster.position, depthForCluster(cluster), layout);
  const movedCluster = v3DagProjectedClusterPosition(cluster, layout);
  return {
    ...projected,
    x: Number((projected.x + movedCluster.x - originalCluster.x).toFixed(2)),
    y: Number((projected.y + movedCluster.y - originalCluster.y).toFixed(2)),
  };
}

function buildLayers(
  deck: DeckModule,
  clusters: readonly DeckClusterDatum[],
  nodes: readonly DeckNodeDatum[],
  edges: readonly DeckEdgeDatum[],
  memberships: readonly DeckMembershipDatum[],
  sliceActive: boolean,
  onSelectNode: (nodeId: string) => void,
  onMoveNode: (nodeId: string, position: V3DagPinnedPosition) => void,
  onDropNode: (nodeId: string, position: V3DagPinnedPosition) => void,
  onMoveCluster: (clusterId: string, position: V3DagPinnedPosition) => void,
): DeckLayer[] {
  const layers: DeckLayer[] = [
    new deck.ScatterplotLayer({
      id: "thread-v3-dag-clusters",
      data: clusters,
      getPosition: (cluster: DeckClusterDatum) => cluster.position,
      getFillColor: (cluster: DeckClusterDatum) => cluster.fillColor,
      getLineColor: (cluster: DeckClusterDatum) => cluster.lineColor,
      getLineWidth: 1.1,
      getRadius: (cluster: DeckClusterDatum) => cluster.radius,
      radiusUnits: "common",
      lineWidthUnits: "pixels",
      stroked: true,
      filled: true,
      opacity: sliceActive ? 0.76 : 0.62,
      pickable: true,
      autoHighlight: true,
      highlightColor: [155, 107, 255, 74],
      onDrag: (info: DeckPickingInfo) => {
        if (!info.object?.id || !info.coordinate) return false;
        onMoveCluster(info.object.id, {
          x: Number(info.coordinate[0].toFixed(2)),
          y: Number(info.coordinate[1].toFixed(2)),
          z: info.object.projected.z,
        });
        return true;
      },
      parameters: { depthTest: false },
    }),
    new deck.LineLayer({
      id: "thread-v3-dag-memberships",
      data: memberships,
      getSourcePosition: (edge: DeckMembershipDatum) => edge.source,
      getTargetPosition: (edge: DeckMembershipDatum) => edge.target,
      getColor: (edge: DeckMembershipDatum) => edge.color,
      getWidth: (edge: DeckMembershipDatum) => edge.width,
      widthUnits: "pixels",
      opacity: 0.88,
      pickable: false,
      parameters: { depthTest: false },
    }),
    new deck.LineLayer({
      id: "thread-v3-dag-edges",
      data: edges,
      getSourcePosition: (edge: DeckEdgeDatum) => edge.source,
      getTargetPosition: (edge: DeckEdgeDatum) => edge.target,
      getColor: (edge: DeckEdgeDatum) => edge.color,
      getWidth: (edge: DeckEdgeDatum) => edge.width,
      widthUnits: "pixels",
      opacity: sliceActive ? 0.64 : 0.54,
      pickable: false,
      parameters: { depthTest: false },
    }),
    new deck.ScatterplotLayer({
      id: "thread-v3-dag-nodes",
      data: nodes,
      getPosition: (node: DeckNodeDatum) => node.position,
      getFillColor: (node: DeckNodeDatum) => node.color,
      getLineColor: (node: DeckNodeDatum) => node.lineColor,
      getLineWidth: (node: DeckNodeDatum) => node.lineWidth,
      getRadius: (node: DeckNodeDatum) => node.radius,
      radiusUnits: "pixels",
      lineWidthUnits: "pixels",
      stroked: true,
      filled: true,
      opacity: 0.96,
      pickable: true,
      autoHighlight: true,
      highlightColor: [15, 255, 198, 96],
      onClick: (info: DeckPickingInfo) => {
        if (!info.object?.id) return false;
        onSelectNode(info.object.id);
        return true;
      },
      onDrag: (info: DeckPickingInfo) => {
        if (!info.object?.id || !info.coordinate) return false;
        onMoveNode(info.object.id, {
          x: Number(info.coordinate[0].toFixed(2)),
          y: Number(info.coordinate[1].toFixed(2)),
          z: info.object.projected.z,
        });
        return true;
      },
      onDragEnd: (info: DeckPickingInfo) => {
        if (!info.object?.id || !info.coordinate) return false;
        onDropNode(info.object.id, {
          x: Number(info.coordinate[0].toFixed(2)),
          y: Number(info.coordinate[1].toFixed(2)),
          z: info.object.projected.z,
        });
        return true;
      },
      parameters: { depthTest: false },
    }),
  ];

  if (deck.TextLayer) {
    layers.push(new deck.TextLayer({
      id: "thread-v3-dag-cluster-labels",
      data: clusters,
      getPosition: (cluster: DeckClusterDatum) => cluster.labelPosition,
      getText: (cluster: DeckClusterDatum) => cluster.label,
      getColor: (cluster: DeckClusterDatum) => cluster.textColor,
      getSize: 9,
      sizeUnits: "pixels",
      getTextAnchor: "middle",
      getAlignmentBaseline: "bottom",
      characterSet: "auto",
      fontFamily: "IBM Plex Mono, ui-monospace, monospace",
      pickable: false,
      parameters: { depthTest: false },
    }));
  }

  return layers;
}

export function V3DeckDagLayer({
  graph,
  visibleNodes,
  visibleEdges,
  litNodeIds,
  selectedNodeId,
  sliceActive,
  litClusterIds,
  filteredNodeIds,
  filteredClusterIds,
  filterActive,
  layout,
  viewState,
  onViewStateChange,
  onSelectNode,
  onMoveNode,
  onDropNode,
  onMoveCluster,
}: {
  graph: V3ProvenanceGraph;
  visibleNodes: readonly V3ProvenanceNode[];
  visibleEdges: readonly V3ProvenanceEdge[];
  litNodeIds: ReadonlySet<string>;
  selectedNodeId: string | null;
  sliceActive: boolean;
  litClusterIds: ReadonlySet<string>;
  filteredNodeIds: ReadonlySet<string> | null;
  filteredClusterIds: ReadonlySet<string>;
  filterActive: boolean;
  layout: V3DagLayoutState;
  viewState: V3DeckViewState;
  onViewStateChange: (viewState: V3DeckViewState) => void;
  onSelectNode: (nodeId: string) => void;
  onMoveNode: (nodeId: string, position: V3DagPinnedPosition) => void;
  onDropNode: (nodeId: string, position: V3DagPinnedPosition) => void;
  onMoveCluster: (clusterId: string, position: V3DagPinnedPosition) => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const deckRef = useRef<InstanceType<DeckConstructor> | null>(null);
  const deckModuleRef = useRef<DeckModule | null>(null);
  const viewStateRef = useRef(viewState);
  const panStartRef = useRef<{
    pointerId: number;
    x: number;
    y: number;
    target: [number, number, number];
    zoom: number;
  } | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "fallback">("loading");

  useEffect(() => {
    viewStateRef.current = viewState;
  }, [viewState]);

  const clusterData = useMemo<DeckClusterDatum[]>(() => graph.clusters.filter((cluster) => cluster.id !== "table").map((cluster: V3ProvenanceCluster) => {
    const lit = litClusterIds.has(cluster.id);
    const inFilter = !filterActive || filteredClusterIds.has(cluster.id) || lit;
    const faded = (sliceActive && !lit) || (filterActive && !inFilter);
    const projected = v3DagProjectedClusterPosition(cluster, layout);
    const filterGhost = filterActive && !inFilter;
    const radiusScale = (0.88 + projected.scale * 0.12) * (filterGhost ? 0.58 : 1);
    return {
      id: cluster.id,
      label: lit ? `${cluster.label} · ${cluster.totalCount.toLocaleString()}` : cluster.label,
      position: [projected.x, projected.y, projected.z],
      projected,
      labelPosition: [projected.x, projected.y - cluster.radius * radiusScale - 18, projected.z],
      fillColor: lit ? [15, 255, 198, 42] : filterGhost ? [24, 30, 36, 4] : faded ? [38, 48, 56, 18] : projected.z > 0 ? [26, 38, 58, 42] : [18, 32, 36, 30],
      lineColor: lit ? [15, 255, 198, 180] : filterGhost ? [64, 76, 90, 18] : faded ? [74, 88, 105, 58] : projected.z > 0 ? [155, 107, 255, 92] : [15, 255, 198, 86],
      textColor: lit ? [15, 255, 198, 220] : filterGhost ? [110, 124, 138, 28] : faded ? [120, 135, 150, 82] : [150, 170, 180, 145],
      radius: cluster.radius * radiusScale,
    };
  }), [filterActive, filteredClusterIds, graph.clusters, layout, litClusterIds, sliceActive]);

  const nodeData = useMemo<DeckNodeDatum[]>(() => visibleNodes.map((node) => {
    const selected = node.id === selectedNodeId;
    const lit = litNodeIds.has(node.id) || selected;
    const inFilter = !filterActive || filteredNodeIds?.has(node.id) || lit;
    const faded = (sliceActive && !lit) || (filterActive && !inFilter);
    const filterGhost = filterActive && !inFilter;
    const baseRadius = 3.1;
    const projected = v3DagProjectedNodePosition(node, graph, layout);
    const membershipCount = layout.memberships[node.id]?.length ?? 0;
    const filterScale = filterGhost ? 0.38 : 1;
    return {
      id: node.id,
      label: node.label,
      position: [projected.x, projected.y, projected.z],
      projected,
      color: filterGhost ? [48, 58, 70, 18] : nodeColor(node, lit, selected, faded),
      lineColor: membershipCount > 0 ? [155, 107, 255, selected ? 255 : filterGhost ? 32 : 210] : selected ? [15, 255, 198, 255] : [4, 8, 10, filterGhost ? 18 : faded ? 42 : 160],
      lineWidth: filterGhost ? 0.18 : selected ? 2.6 : membershipCount > 0 ? 1.8 : lit ? 1.25 : 0.35,
      radius: (selected ? 7.4 : lit ? 4.25 : baseRadius) * Math.max(0.78, projected.scale) * filterScale,
    };
  }), [filterActive, filteredNodeIds, graph, layout, litNodeIds, selectedNodeId, sliceActive, visibleNodes]);

  const edgeData = useMemo<DeckEdgeDatum[]>(() => visibleEdges.flatMap((edge) => {
    const source = graph.nodesById.get(edge.from);
    const target = graph.nodesById.get(edge.to);
    if (!source || !target) return [];
    const lit = litNodeIds.has(edge.from) && litNodeIds.has(edge.to);
    const inFilter = !filterActive || Boolean(filteredNodeIds?.has(edge.from) && filteredNodeIds.has(edge.to));
    const faded = (sliceActive && !lit) || (filterActive && !inFilter);
    const filterGhost = filterActive && !inFilter;
    const sourcePosition = v3DagProjectedNodePosition(source, graph, layout);
    const targetPosition = v3DagProjectedNodePosition(target, graph, layout);
    return [{
      id: edge.id,
      source: [sourcePosition.x, sourcePosition.y, sourcePosition.z],
      target: [targetPosition.x, targetPosition.y, targetPosition.z],
      color: filterGhost ? [54, 66, 78, 12] : edgeColor(lit, faded),
      width: lit ? 0.92 : filterGhost ? 0.18 : faded ? 0.34 : 0.68,
    }];
  }), [filterActive, filteredNodeIds, graph, layout, litNodeIds, sliceActive, visibleEdges]);

  const membershipData = useMemo<DeckMembershipDatum[]>(() => {
    const visibleIds = new Set(visibleNodes.map((node) => node.id));
    return Object.entries(layout.memberships).flatMap(([nodeId, clusterIds]) => {
      if (!visibleIds.has(nodeId)) return [];
      const node = graph.nodesById.get(nodeId);
      if (!node) return [];
      const inFilter = !filterActive || filteredNodeIds?.has(nodeId);
      const source = v3DagProjectedNodePosition(node, graph, layout);
      return clusterIds.flatMap((clusterId) => {
        const cluster = graph.clusters.find((item) => item.id === clusterId);
        if (!cluster) return [];
        const target = v3DagProjectedClusterPosition(cluster, layout);
        return [{
          id: `membership:${nodeId}:${clusterId}`,
          source: [source.x, source.y, source.z] as [number, number, number],
          target: [target.x, target.y, target.z] as [number, number, number],
          color: [155, 107, 255, inFilter ? 132 : 38] as [number, number, number, number],
          width: inFilter ? 0.72 : 0.32,
        }];
      });
    });
  }, [filterActive, filteredNodeIds, graph, layout, visibleNodes]);

  function handlePointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    if (event.button !== 0) return;
    if (event.target !== event.currentTarget) return;
    panStartRef.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      target: viewState.target,
      zoom: viewState.zoom,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    const start = panStartRef.current;
    if (!start || start.pointerId !== event.pointerId) return;
    const scale = 2 ** start.zoom;
    onViewStateChange({
      target: [
        start.target[0] - (event.clientX - start.x) / scale,
        start.target[1] - (event.clientY - start.y) / scale,
        start.target[2],
      ],
      zoom: start.zoom,
    });
  }

  function handlePointerUp(event: ReactPointerEvent<HTMLDivElement>) {
    if (panStartRef.current?.pointerId === event.pointerId) {
      panStartRef.current = null;
    }
  }

  function handleWheel(event: ReactWheelEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();

    const rect = event.currentTarget.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const pointerX = event.clientX - rect.left;
    const pointerY = event.clientY - rect.top;
    const deltaModeScale = event.deltaMode === 1 ? 18 : event.deltaMode === 2 ? 240 : 1;
    const currentScale = 2 ** viewState.zoom;
    const nextZoom = clamp(viewState.zoom - event.deltaY * deltaModeScale * 0.0022, -1.5, 6);
    const nextScale = 2 ** nextZoom;
    const worldUnderPointerX = viewState.target[0] + (pointerX - centerX) / currentScale;
    const worldUnderPointerY = viewState.target[1] + (pointerY - centerY) / currentScale;

    onViewStateChange({
      target: [
        worldUnderPointerX - (pointerX - centerX) / nextScale,
        worldUnderPointerY - (pointerY - centerY) / nextScale,
        viewState.target[2],
      ],
      zoom: nextZoom,
    });
  }

  useEffect(() => {
    let cancelled = false;
    loadDeckGl()
      .then((deck) => {
        if (cancelled || !containerRef.current) return;
        deckModuleRef.current = deck;
        deckRef.current = new deck.Deck({
          parent: containerRef.current,
          views: [new deck.OrthographicView({ id: "thread-v3-dag-view", controller: true })],
          viewState: viewStateRef.current,
          controller: {
            dragPan: true,
            scrollZoom: true,
            doubleClickZoom: true,
            touchZoom: true,
            keyboard: true,
          },
          onViewStateChange: ({ viewState: next }: { viewState?: Partial<V3DeckViewState> }) => {
            const normalized = normalizeViewState(next, viewStateRef.current);
            onViewStateChange(normalized);
            return normalized;
          },
          useDevicePixels: false,
          layers: buildLayers(deck, clusterData, nodeData, edgeData, membershipData, sliceActive, onSelectNode, onMoveNode, onDropNode, onMoveCluster),
        });
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("fallback");
      });

    return () => {
      cancelled = true;
      deckRef.current?.finalize();
      deckRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!deckRef.current) return;
    deckRef.current.setProps({ viewState });
  }, [viewState]);

  useEffect(() => {
    const deck = deckModuleRef.current;
    if (!deckRef.current || !deck) return;
    deckRef.current.setProps({
      views: [new deck.OrthographicView({ id: "thread-v3-dag-view", controller: true })],
      controller: {
        dragPan: true,
        scrollZoom: true,
        doubleClickZoom: true,
        touchZoom: true,
        keyboard: true,
      },
      onViewStateChange: ({ viewState: next }: { viewState?: Partial<V3DeckViewState> }) => {
        const normalized = normalizeViewState(next, viewStateRef.current);
        onViewStateChange(normalized);
        return normalized;
      },
      layers: buildLayers(deck, clusterData, nodeData, edgeData, membershipData, sliceActive, onSelectNode, onMoveNode, onDropNode, onMoveCluster),
    });
  }, [clusterData, edgeData, membershipData, nodeData, onDropNode, onMoveCluster, onMoveNode, onSelectNode, onViewStateChange, sliceActive]);

  return (
    <div
      className={`v3-deck-dag v3-deck-dag-${status}`}
      data-testid="v3-deck-dag"
      data-zoom={viewState.zoom.toFixed(3)}
      data-target={`${viewState.target[0].toFixed(1)},${viewState.target[1].toFixed(1)}`}
      data-projection={layout.projectionMode}
      data-filter-active={filterActive ? "true" : "false"}
      data-filter-nodes={filteredNodeIds?.size ?? 0}
      data-node-pins={Object.keys(layout.nodePins).length}
      data-cluster-pins={Object.keys(layout.clusterPins).length}
      ref={containerRef}
      aria-hidden="true"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onWheel={handleWheel}
    >
      {status !== "ready" && <span>{status === "loading" ? "loading WebGL DAG" : "DAG fallback unavailable"}</span>}
    </div>
  );
}
